import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import { ShiftEventsResponse } from "shifts/models";

// Event history for a single shift — the drill-in behind the Shift ID link on the update
// grid, ported from the legacy YardDailyEventDetail page. Read-only: this is where you find
// out what the driver's app actually recorded and who touched it since.

const emptyShift: ShiftEventsResponse = {
  yardDailyId: 0,
  shiftName: "",
  location: "",
  shiftDate: null,
  events: [],
};

const ShiftEventDetail = () => {
  const { yardDailyId } = useParams();
  const [shift, setShift] = useState<ShiftEventsResponse>(emptyShift);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const id = Number(yardDailyId);
    if (!id) return;

    setIsLoading(true);
    shiftManagementService
      .getShiftEvents(id)
      .then(setShift)
      .catch(() => toast.error("Could not load shift events."))
      .finally(() => setIsLoading(false));
  }, [yardDailyId]);

  const columns: GridColDef[] = [
    { field: "eventName", headerName: "Event", width: 160, sortable: false },
    { field: "eventId", headerName: "Event ID", width: 100, sortable: false },
    {
      field: "eventTime",
      headerName: "Event Time",
      width: 150,
      sortable: false,
      renderCell: (params) => formatDateTime(params.row.eventTime),
    },
    { field: "driverName", headerName: "Driver", width: 160, sortable: false },
    { field: "comments", headerName: "Comments", width: 200, sortable: false },
    { field: "odometer", headerName: "Odometer", width: 100, sortable: false, align: "right", headerAlign: "right" },
    {
      field: "latitude",
      headerName: "Lat / Long",
      width: 170,
      sortable: false,
      renderCell: (params) =>
        params.row.latitude != null ? `${params.row.latitude}, ${params.row.longitude}` : "",
    },
    { field: "routeLocation", headerName: "Route Location", width: 160, sortable: false },
    { field: "closingComments", headerName: "Closing Comments", width: 180, sortable: false },
    {
      field: "createDateTime",
      headerName: "Created",
      width: 150,
      sortable: false,
      renderCell: (params) => formatDateTime(params.row.createDateTime),
    },
    { field: "dataSourceName", headerName: "Data Source", width: 130, sortable: false },
    { field: "createUser", headerName: "Create User", width: 140, sortable: false },
    { field: "updateUser", headerName: "Update User", width: 140, sortable: false },
    { field: "auditProcessName", headerName: "Audit Process", width: 190, sortable: false },
    {
      field: "sourceProvidedGps",
      headerName: "Source GPS",
      width: 110,
      sortable: false,
      renderCell: (params) => (params.row.sourceProvidedGps == null ? "" : params.row.sourceProvidedGps ? "Yes" : "No"),
    },
    { field: "hubName", headerName: "Driver Hub", width: 140, sortable: false },
  ];

  return (
    <Box sx={{ px: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Button component={Link} to="/shifts/dashboard/update" startIcon={<ArrowBackIcon />} size="small">
          Back to Shift Update
        </Button>
      </Stack>

      <Typography variant="h5">Shift {shift.yardDailyId || yardDailyId} — Event History</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {[shift.shiftName, shift.location, shift.shiftDate ? moment(shift.shiftDate).format("MM/DD/YYYY") : ""]
          .filter(Boolean)
          .join(" · ")}
      </Typography>

      <Paper sx={{ height: "calc(100vh - 280px)", minHeight: 360 }}>
        <DataGrid
          rows={shift.events}
          columns={columns}
          getRowId={(row) => row.eventId}
          loading={isLoading}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
          sx={{ fontSize: ".85em" }}
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No events recorded for this shift.
              </Stack>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};

const formatDateTime = (value: string | null): string =>
  value ? moment(value).format("MM/DD/YYYY HH:mm") : "";

export default ShiftEventDetail;
